import { useState, useEffect, useCallback, useRef } from "react";
import { useAdminAuth } from "@/lib/adminAuth";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

interface CarouselLogo {
  id: number;
  name: string;
  imageData: string | null;
  sortOrder: number;
  active: boolean;
}

export default function CarouselTab() {
  const { adminFetch } = useAdminAuth();
  const [logos, setLogos] = useState<CarouselLogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reordering, setReordering] = useState<number | null>(null);
  const [uploading, setUploading] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<{ id: number; value: string } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("api/admin/carousel-logos");
      if (!res.ok) throw new Error("Failed to load");
      setLogos(await res.json());
    } catch (e: unknown) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { load(); }, [load]);

  const move = async (idx: number, direction: "up" | "down") => {
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= logos.length) return;
    const next = [...logos];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setLogos(next);
    setReordering(next[swapIdx].id);
    try {
      const res = await adminFetch("api/admin/carousel-logos/reorder", {
        method: "PUT",
        body: JSON.stringify({ order: next.map((l) => l.id) }),
      });
      if (!res.ok) throw new Error("Reorder failed");
    } catch (e: unknown) {
      alert(errMsg(e));
      await load();
    } finally {
      setReordering(null);
    }
  };

  const handleImageUpload = async (id: number, file: File) => {
    setUploading(id);
    try {
      const reader = new FileReader();
      const imageData = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await adminFetch(`api/admin/carousel-logos/${id}`, {
        method: "PUT",
        body: JSON.stringify({ imageData }),
      });
      if (!res.ok) throw new Error("Upload failed");
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveImage = async (id: number) => {
    setUploading(id);
    try {
      const res = await adminFetch(`api/admin/carousel-logos/${id}`, {
        method: "PUT",
        body: JSON.stringify({ imageData: null }),
      });
      if (!res.ok) throw new Error("Failed to remove image");
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    } finally {
      setUploading(null);
    }
  };

  const handleRename = async (id: number, name: string) => {
    try {
      const res = await adminFetch(`api/admin/carousel-logos/${id}`, {
        method: "PUT",
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error("Rename failed");
      setEditingName(null);
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    }
  };

  const handleToggleActive = async (logo: CarouselLogo) => {
    try {
      const res = await adminFetch(`api/admin/carousel-logos/${logo.id}`, {
        method: "PUT",
        body: JSON.stringify({ active: !logo.active }),
      });
      if (!res.ok) throw new Error("Toggle failed");
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await adminFetch(`api/admin/carousel-logos/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setDeleteId(null);
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await adminFetch("api/admin/carousel-logos", {
        method: "POST",
        body: JSON.stringify({ name: newName.trim(), sortOrder: logos.length + 1 }),
      });
      if (!res.ok) throw new Error("Add failed");
      setNewName("");
      await load();
    } catch (e: unknown) {
      alert(errMsg(e));
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <div className="text-white/50 py-8 text-center">Loading carousel logos…</div>;
  if (error) return <div className="text-red-400 py-8 text-center">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Carousel Logos ({logos.length})</h2>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10 mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/50 text-left">
              <th className="px-3 py-3 font-medium w-16 text-center">Order</th>
              <th className="px-4 py-3 font-medium w-20">Logo</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Active</th>
              <th className="px-4 py-3 font-medium">Image</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {logos.map((logo, idx) => (
              <tr
                key={logo.id}
                className={`border-b border-white/5 transition ${reordering === logo.id ? "opacity-60" : "hover:bg-white/5"}`}
              >
                <td className="px-3 py-3">
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      onClick={() => move(idx, "up")}
                      disabled={idx === 0 || reordering !== null}
                      className="text-white/30 hover:text-white/80 disabled:opacity-20 disabled:cursor-not-allowed transition p-0.5 rounded"
                      title="Move up"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <span className="text-white/25 text-[10px] leading-none font-mono">{idx + 1}</span>
                    <button
                      onClick={() => move(idx, "down")}
                      disabled={idx === logos.length - 1 || reordering !== null}
                      className="text-white/30 hover:text-white/80 disabled:opacity-20 disabled:cursor-not-allowed transition p-0.5 rounded"
                      title="Move down"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                    {logo.imageData ? (
                      <img
                        src={logo.imageData}
                        alt={logo.name}
                        className="w-full h-full object-contain p-1"
                      />
                    ) : (
                      <span className="text-white/20 text-[9px] text-center leading-tight px-1">No image</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {editingName?.id === logo.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={editingName.value}
                        onChange={(e) => setEditingName({ id: logo.id, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRename(logo.id, editingName.value);
                          if (e.key === "Escape") setEditingName(null);
                        }}
                        className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-purple-500 w-36"
                        autoFocus
                      />
                      <button
                        onClick={() => handleRename(logo.id, editingName.value)}
                        className="text-green-400 hover:text-green-300 text-xs"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingName(null)}
                        className="text-white/40 hover:text-white/70 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingName({ id: logo.id, value: logo.name })}
                      className="text-white font-medium hover:text-purple-300 transition text-left"
                      title="Click to rename"
                    >
                      {logo.name}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleActive(logo)}
                    className={`w-10 h-5 rounded-full transition-colors ${logo.active ? "bg-purple-600" : "bg-white/20"} relative`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${logo.active ? "left-5" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      ref={(el) => { fileInputRefs.current[logo.id] = el; }}
                      type="file"
                      accept="image/png,image/svg+xml"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(logo.id, file);
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() => fileInputRefs.current[logo.id]?.click()}
                      disabled={uploading === logo.id}
                      className="text-purple-400 hover:text-purple-300 transition text-xs border border-purple-400/30 hover:border-purple-300/50 rounded px-2 py-1 disabled:opacity-50"
                    >
                      {uploading === logo.id ? "Uploading…" : logo.imageData ? "Replace" : "Upload"}
                    </button>
                    {logo.imageData && (
                      <button
                        onClick={() => handleRemoveImage(logo.id)}
                        disabled={uploading === logo.id}
                        className="text-orange-400 hover:text-orange-300 transition text-xs border border-orange-400/30 hover:border-orange-300/50 rounded px-2 py-1 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setDeleteId(logo.id)}
                    className="text-red-400 hover:text-red-300 transition text-xs border border-red-400/30 hover:border-red-300/50 rounded px-2 py-1"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <h3 className="text-white/70 text-sm font-medium mb-3">Add New Entry</h3>
        <div className="flex items-center gap-3">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="Logo name (e.g. Salesforce)"
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 placeholder-white/30"
          />
          <button
            onClick={handleAdd}
            disabled={adding || !newName.trim()}
            className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg transition"
          >
            {adding ? "Adding…" : "+ Add"}
          </button>
        </div>
      </div>

      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131320] border border-white/10 rounded-2xl p-6 w-full max-w-sm text-center">
            <p className="text-white font-medium mb-2">Delete this logo entry?</p>
            <p className="text-white/50 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-white/20 text-white/70 py-2 rounded-lg hover:bg-white/5 transition text-sm">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded-lg transition text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
